import { useMutation, useQuery, useQueryClient } from "react-query";

import axiosInstance from "../config/Axios";
import { useCustomToast } from "../pages/Contexts/ToastContext";

// ── Settings ────────────────────────────────────────────────────────

const getPrayerSettings = async () => {
    const { data } = await axiosInstance.get("/prayer/settings");
    return data?.data;
};

export const usePrayerSettings = () => {
    const { isLoading, data, refetch } = useQuery(
        "getPrayerSettings",
        getPrayerSettings,
        { staleTime: 60_000 }
    );
    return { isLoading, data, refetch };
};

export const useUpdatePrayerSettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async (payload) => {
            const { data } = await axiosInstance.put("/prayer/settings", payload);
            return data?.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("getPrayerSettings");
                queryClient.invalidateQueries("getTodaysPrayerTimes");
                showToast("success", "Prayer settings", "Saved.");
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Prayer settings",
                    error?.response?.data?.message || "Failed to save settings."
                );
            },
        }
    );
};

// ── Today's prayer times ────────────────────────────────────────────

const getTodaysPrayerTimes = async () => {
    const { data } = await axiosInstance.get("/prayer/times/today");
    return data?.data;
};

export const useTodaysPrayerTimes = ({ refetchInterval = false } = {}) => {
    const { isLoading, data, refetch, isFetching } = useQuery(
        "getTodaysPrayerTimes",
        getTodaysPrayerTimes,
        {
            refetchInterval,
            staleTime: 5 * 60_000,
        }
    );
    return { isLoading, data, refetch, isFetching };
};

// ── Test prayer pause ───────────────────────────────────────────────

export const useTestPrayerPause = () => {
    const { showToast } = useCustomToast();

    return useMutation(
        async ({ durationSeconds = 30, deviceIds = null } = {}) => {
            const payload = { duration_seconds: durationSeconds };
            if (deviceIds && deviceIds.length) payload.device_ids = deviceIds;
            const { data } = await axiosInstance.post("/prayer/test", payload);
            return data?.data;
        },
        {
            onSuccess: (result) => {
                const count = result?.device_count ?? 0;
                showToast(
                    "success",
                    "Test prayer pause",
                    count > 0
                        ? `Sent to ${count} screen${count === 1 ? "" : "s"}.`
                        : "Sent (no devices online to confirm)."
                );
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Test prayer pause",
                    error?.response?.data?.message || "Failed to send test."
                );
            },
        }
    );
};

// ── Per-device overrides (v3) ───────────────────────────────────────

const getPrayerExceptions = async () => {
    const { data } = await axiosInstance.get("/prayer/exceptions");
    return data?.data;
};

export const usePrayerExceptions = () => {
    const { isLoading, data, refetch, isFetching } = useQuery(
        "getPrayerExceptions",
        getPrayerExceptions,
        { staleTime: 30_000 }
    );
    return { isLoading, data, refetch, isFetching };
};

const getEffectiveDevicePrayer = async (deviceId) => {
    const { data } = await axiosInstance.get(`/prayer/device/${deviceId}/effective`);
    return data?.data;
};

export const useEffectiveDevicePrayer = (deviceId, enabled = true) => {
    const { isLoading, data, refetch } = useQuery(
        ["getEffectiveDevicePrayer", deviceId],
        () => getEffectiveDevicePrayer(deviceId),
        { enabled: enabled && !!deviceId, staleTime: 30_000 }
    );
    return { isLoading, data, refetch };
};

export const useUpsertDeviceOverride = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async ({ deviceId, payload }) => {
            const { data } = await axiosInstance.post(
                `/prayer/device/${deviceId}/override`,
                payload
            );
            return data?.data;
        },
        {
            onSuccess: (_, { deviceId }) => {
                queryClient.invalidateQueries("getPrayerExceptions");
                queryClient.invalidateQueries(["getEffectiveDevicePrayer", deviceId]);
                showToast("success", "Device exception", "Saved.");
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Device exception",
                    error?.response?.data?.message || "Failed to save exception."
                );
            },
        }
    );
};

export const useDeleteDeviceOverride = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async (deviceId) => {
            const { data } = await axiosInstance.delete(
                `/prayer/device/${deviceId}/override`
            );
            return data?.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("getPrayerExceptions");
                showToast("success", "Device exception", "Removed.");
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Device exception",
                    error?.response?.data?.message || "Failed to remove exception."
                );
            },
        }
    );
};

// ── City autocomplete (OpenStreetMap Nominatim) ─────────────────────
// Free, no API key. Rate-limited to 1 req/sec — we debounce in the UI.

export const useCitySearch = (queryString, enabled) => {
    const { isLoading, isFetching, data } = useQuery(
        ["citySearch", queryString],
        async ({ signal }) => {
            const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&accept-language=en&q=${encodeURIComponent(queryString)}`;
            // Pass the React Query AbortSignal so a fast-typing user
            // cancels in-flight Nominatim calls instead of stacking
            // them up and racing.
            const res = await fetch(url, {
                headers: { "Accept": "application/json" },
                signal,
            });
            if (!res.ok) return [];
            const rows = await res.json();
            return rows.map((r) => ({
                label: r.display_name,
                city: r.address?.city || r.address?.town || r.address?.village || r.name,
                country: r.address?.country,
                country_code: r.address?.country_code,
                lat: parseFloat(r.lat),
                lng: parseFloat(r.lon),
            }));
        },
        {
            enabled: !!enabled && !!queryString && queryString.length >= 2,
            staleTime: 5 * 60_000,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );
    return { isLoading, isFetching, data: data || [] };
};
